import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2, Trash2, UserPlus, Users } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';
import { cn } from '@/lib/utils';
import { supplyPointQueryKeys } from '@/features/supply-points/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';
import { useInviteUserDialog } from '../invite-user-dialog/useInviteUserDialog';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { avatarUrl } from '@/common/utils/avatarUrl';
import type { Access, OrgWithMembers, RoleEnum } from '@/features/supply-points/services/schemas';

type Props = {
  supplyPointId: number;
  accesses: Access[];
  orgAccesses: OrgWithMembers[];
  search: string;
  totalPeople: number;
};

export function PeopleTab({ supplyPointId, accesses, orgAccesses, search, totalPeople }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const openInviteDialog = useInviteUserDialog();

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: RoleEnum }) =>
      SupplyPointService.partialUpdateAccess(id, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all }),
  });

  const removeAccessMutation = useMutation({
    mutationFn: (id: number) => SupplyPointService.deleteAccess(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all }),
  });

  const handleRemoveAccess = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: t`Remove access?`,
      description: t`${name} will lose access to this supply point.`,
      confirmLabel: t`Remove`,
    });
    if (confirmed) removeAccessMutation.mutate(id);
  };

  const lowerSearch = search.toLowerCase();

  const filteredAccesses = accesses.filter((a) =>
    `${a.user.firstName} ${a.user.lastName} ${a.user.email}`.toLowerCase().includes(lowerSearch)
  );

  const orgMembers = orgAccesses.flatMap((orgAccess) =>
    orgAccess.members.map((m) => ({ ...m, orgId: orgAccess.id, orgName: orgAccess.name }))
  );

  const filteredOrgMembers = orgMembers.filter((m) =>
    `${m.user.firstName} ${m.user.lastName} ${m.user.email}`.toLowerCase().includes(lowerSearch)
  );

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
        <p className="text-sm text-muted-foreground">
          {totalPeople} <Trans>people with access</Trans>
        </p>
        <Button onClick={() => openInviteDialog(supplyPointId)} className="gap-1.5" size="sm">
          <UserPlus size={14} />
          <Trans>Invite user</Trans>
        </Button>
      </div>

      {/* Direct members */}
      {filteredAccesses.length > 0 && (
        <>
          <div className="px-5 py-2 bg-muted/30 border-b border-border/50">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Trans>Direct members</Trans> · {filteredAccesses.length}
            </span>
          </div>
          <div className="hidden sm:grid grid-cols-[1fr_160px_140px_48px] gap-4 px-5 py-2.5 bg-muted/20 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span>
              <Trans>User</Trans>
            </span>
            <span>
              <Trans>Role</Trans>
            </span>
            <span>
              <Trans>Status</Trans>
            </span>
            <span />
          </div>
          {filteredAccesses.map((access, idx) => (
            <div
              key={access.id}
              className={cn(
                'flex sm:grid sm:grid-cols-[1fr_160px_140px_48px] gap-3 sm:gap-4 items-center px-5 py-3.5',
                idx < filteredAccesses.length - 1 || filteredOrgMembers.length > 0
                  ? 'border-b border-border'
                  : ''
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="size-8 rounded-full bg-muted overflow-hidden shrink-0 border border-border">
                  <img
                    src={avatarUrl(access.user.firstName, access.user.lastName, access.user.email)}
                    alt={
                      [access.user.firstName, access.user.lastName].filter(Boolean).join(' ') ||
                      access.user.email
                    }
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {access.user.firstName} {access.user.lastName}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{access.user.email}</div>
                </div>
              </div>
              <div>
                <Select
                  value={access.role}
                  onValueChange={(v) =>
                    updateRoleMutation.mutate({ id: access.id, role: v as RoleEnum })
                  }
                  disabled={updateRoleMutation.isPending}
                >
                  <SelectTrigger className="h-8 text-xs w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <Trans>Admin</Trans>
                    </SelectItem>
                    <SelectItem value="write">
                      <Trans>Editor</Trans>
                    </SelectItem>
                    <SelectItem value="read">
                      <Trans>Reader</Trans>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden sm:flex" />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() =>
                    handleRemoveAccess(
                      access.id,
                      `${access.user.firstName} ${access.user.lastName}`.trim() || access.user.email
                    )
                  }
                  className="rounded-lg p-1.5 text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Org-inherited members */}
      {filteredOrgMembers.length > 0 && (
        <>
          <div className="px-5 py-2 bg-violet-50/60 border-b border-violet-100/60 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-600 flex items-center gap-1.5">
              <Building2 size={11} />
              <Trans>Via organizations</Trans> · {filteredOrgMembers.length}
            </span>
            <span className="text-xs text-violet-400">
              <Trans>Roles are managed at the organization level</Trans>
            </span>
          </div>
          {filteredOrgMembers.map((member, idx) => (
            <div
              key={`${member.orgId}-${member.id}`}
              className={cn(
                'flex items-center gap-3 px-5 py-3.5',
                idx < filteredOrgMembers.length - 1 ? 'border-b border-border/50' : ''
              )}
            >
              <div className="size-8 rounded-full bg-muted overflow-hidden shrink-0 border border-border">
                <img
                  src={avatarUrl(member.user.firstName, member.user.lastName, member.user.email)}
                  alt={
                    [member.user.firstName, member.user.lastName].filter(Boolean).join(' ') ||
                    member.user.email
                  }
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-muted-foreground truncate">
                  {member.user.firstName} {member.user.lastName}
                </div>
                <div className="text-xs text-muted-foreground/70 truncate">{member.user.email}</div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 bg-violet-50 text-violet-700 text-xs font-medium rounded-md border border-violet-100">
                <Building2 size={11} />
                {member.orgName}
              </span>
              <span className="text-xs text-muted-foreground/60 italic">
                <Trans>via org</Trans>
              </span>
            </div>
          ))}
        </>
      )}

      {filteredAccesses.length === 0 && filteredOrgMembers.length === 0 && (
        <div className="py-16 text-center">
          <Users size={32} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">
            {search ? <Trans>No people found</Trans> : <Trans>No users have access yet</Trans>}
          </p>
        </div>
      )}
    </div>
  );
}
